import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const TurnstileWidget = forwardRef(({ onToken, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const cbRef = useRef({ onToken, onError });
  cbRef.current = { onToken, onError };

  useEffect(() => {
    let cancelled = false;

    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITEKEY,
        action: "contact",
        theme: "dark",
        appearance: "interaction-only",
        execution: "execute",
        callback: (token) => cbRef.current.onToken?.(token),
        "error-callback": () => cbRef.current.onError?.("error"),
        "expired-callback": () => cbRef.current.onError?.("expired"),
        "timeout-callback": () => cbRef.current.onError?.("timeout"),
      });
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (window.turnstile) {
      render();
    } else if (existing) {
      existing.addEventListener("load", render);
    } else {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = render;
      script.onerror = () => cbRef.current.onError?.("load-failed");
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      existing?.removeEventListener("load", render);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    execute: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.execute(widgetIdRef.current);
        return true;
      }
      return false;
    },
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  return <div ref={containerRef} className="mt-4 empty:mt-0 flex justify-center" data-testid="turnstile-widget" />;
});
